/**
 * OTA updater — native driver (SPIKE, issue #1334).
 *
 * Wires @capgo/capacitor-updater into the Capacitor build so viteclient
 * (web/JS/HTML/CSS) changes can reach installed app users without a store
 * release. The *decision* logic lives in `./manifest.ts`; this file does the
 * platform side effects (notifyAppReady / download / set / reset) and is a
 * strict no-op on the web build.
 *
 * Boundary: OTA ships the web layer ONLY. Native changes (new Capacitor
 * plugins, permissions, SDK bumps) still require a store release — that's
 * enforced by the `minNativeBuild` gate in the manifest (see manifest.ts).
 *
 * Safety model (scope item #4):
 *   - `autoUpdate:false` + `statsUrl:''` (capacitor.config.ts): we drive every
 *     step ourselves and nothing phones home to Capgo cloud.
 *   - After a bundle is set(), Capgo starts a watchdog. If the freshly-loaded
 *     bundle does NOT call `notifyAppReady()` within `appReadyTimeout`, Capgo
 *     auto-rolls-back to the previous good bundle (crash-loop / white-screen
 *     protection). We call notifyAppReady() as early as possible on every boot.
 *   - Any thrown error in the check/download path is swallowed: a bad manifest
 *     or failed download must never brick the app, only skip the update.
 *
 * This is prototype wiring for the spike. It is gated behind
 * `VITE_OTA_ENABLED` and is not part of the production release flow.
 */

import { IS_SERVER } from '@/renderer/utils/ssr';

import { type OtaManifest, selectUpdate } from './manifest';

/** Where the host publishes the manifest. Overridable for local/staging tests. */
const MANIFEST_URL =
  import.meta.env.VITE_OTA_MANIFEST_URL || 'https://vcdn.raspicks.com/ota/latest.json';

/** Master switch for the spike. Off by default so nothing changes for prod. */
const OTA_ENABLED = import.meta.env.VITE_OTA_ENABLED === 'true';

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
};

function nativeCapacitor(): CapacitorGlobal | undefined {
  if (IS_SERVER) {
    return undefined;
  }
  const cap = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
  return cap?.isNativePlatform?.() ? cap : undefined;
}

let started = false;

/**
 * Idempotent entry point. Call once per app launch (from a client-only hook).
 * Marks the running bundle healthy, then checks for an update on launch and on
 * every resume from background.
 */
export async function initOta(): Promise<void> {
  // Master kill-switch. When VITE_OTA_ENABLED !== 'true' the updater is
  // genuinely inert: no manifest fetch, no plugin import, no resume listener. A
  // build shipped with the flag unset (every store build today) does nothing
  // here — "flag off = inert" is now actually enforced, not just assumed.
  if (!OTA_ENABLED) {
    return;
  }
  if (started) {
    return;
  }
  const cap = nativeCapacitor();
  if (!cap) {
    return; // web build / SSR — no-op
  }
  // Android only. The manifest at MANIFEST_URL is a single, platform-agnostic
  // pointer published by the Android OTA workflow, and `minNativeBuild` is
  // compared against the running shell's build number — an iOS CFBundleVersion
  // (4011) and an Android versionCode (45xx) share that one number space, so an
  // OTA-enabled iOS shell would happily install the Android-published bundle.
  // Shipping web code to iOS outside App Store review is also exactly what
  // review guideline 3.1.1 / 2.5.2 is about, and iOS OTA is blocked on approval
  // anyway (#1232). Gate it here rather than relying on the build flag, so an
  // iOS build made with the same `build:mobile` command is inert. Revisit as a
  // per-platform manifest when iOS OTA is actually wanted.
  if (cap.getPlatform?.() !== 'android') {
    return;
  }
  started = true;

  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');

    // 1. Tell Capgo the current bundle booted fine, BEFORE the watchdog fires.
    //    This is what commits a freshly-applied OTA bundle as "last good" and
    //    cancels the auto-rollback timer.
    await CapacitorUpdater.notifyAppReady();

    // 2. Activate a bundle staged via next() on a PRIOR launch. In manual mode
    //    (autoUpdate:false) Capgo does not swap a queued bundle in on its own at
    //    cold start, so a routine non-mandatory publish would otherwise loop —
    //    re-downloading every launch and never applying. Consume it here, now
    //    that the running bundle is committed as last-good above. (No-op when
    //    nothing is staged.)
    await activatePendingBundle(CapacitorUpdater);

    // 3. Check now (cold start) ...
    await checkForOtaUpdate();

    // 4. ... and again whenever the app returns to the foreground.
    const { App } = await import('@capacitor/app');
    App.addListener('resume', () => {
      void checkForOtaUpdate();
    });
  } catch (err) {
    // Plugin missing, import failed, etc. Never fatal.
    console.warn('[ota] init failed (non-fatal)', err);
  }
}

/**
 * Activate a bundle staged via next() on a previous launch (manual-mode apply).
 *
 * With autoUpdate:false, Capgo does not swap a queued "next" bundle in on its
 * own at cold start, so we do it explicitly: if a pending bundle exists, is not
 * already running, and did not previously fail its watchdog, set() it. set() is
 * terminal — it destroys the JS context and reloads — so this runs BEFORE
 * checkForOtaUpdate() and nothing after the set() call executes. The fresh boot
 * re-enters initOta(), commits the new bundle via notifyAppReady(), and the
 * watchdog reverts it if it white-screens. Fully fail-safe: any error just
 * leaves the current bundle running.
 */
async function activatePendingBundle(
  // Typed loosely to avoid importing the plugin's types into the web build.
  updater: typeof import('@capgo/capacitor-updater').CapacitorUpdater,
): Promise<void> {
  try {
    const pending = await updater.getNextBundle();
    // Nothing queued, or the queued bundle already failed its watchdog and was
    // marked errored (never re-activate a known-bad bundle — no reboot loop).
    if (!pending || pending.status === 'error') {
      return;
    }

    const current = await updater.current();
    if (current?.bundle?.id === pending.id) {
      return; // already the running bundle
    }

    console.info(`[ota] activating staged bundle ${pending.version}`);
    await updater.set({ id: pending.id }); // terminal: reloads, nothing below runs
  } catch (err) {
    console.warn('[ota] activate staged bundle failed (non-fatal)', err);
  }
}

/**
 * One check cycle: fetch manifest -> decide -> download+apply, all fail-safe.
 * Exported for the resume listener and for manual "check now" UX later.
 */
export async function checkForOtaUpdate(): Promise<void> {
  const cap = nativeCapacitor();
  if (!cap) {
    return;
  }

  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
    const { App } = await import('@capacitor/app');

    const [manifestRes, current, info] = await Promise.all([
      fetch(MANIFEST_URL, { cache: 'no-store' }),
      CapacitorUpdater.current(),
      App.getInfo(),
    ]);

    if (!manifestRes.ok) {
      return; // host down / 404 — skip quietly
    }
    const raw = await manifestRes.json();

    const decision = selectUpdate({
      raw,
      currentVersion: current?.bundle?.version ?? 'builtin',
      nativeBuild: Number.parseInt(info?.build ?? '0', 10) || 0,
    });

    if (decision.action === 'skip') {
      console.info(`[ota] no update: ${decision.reason}`, decision.detail ?? '');
      return;
    }

    await applyBundle(CapacitorUpdater, decision.manifest);
  } catch (err) {
    // Network error, malformed JSON, download failure — all non-fatal.
    console.warn('[ota] check failed (non-fatal)', err);
  }
}

/**
 * Download + verify + activate a bundle. Capgo verifies `checksum` during
 * download and throws on mismatch, so a corrupt/tampered zip never installs.
 * On any failure we delete the partial bundle and leave the running one intact.
 */
async function applyBundle(
  // Typed loosely to avoid importing the plugin's types into the web build.
  updater: typeof import('@capgo/capacitor-updater').CapacitorUpdater,
  manifest: OtaManifest,
): Promise<void> {
  let downloadedId: string | undefined;
  try {
    const bundle = await updater.download({
      url: manifest.url,
      version: manifest.version,
      checksum: manifest.checksum,
    });
    downloadedId = bundle.id;

    if (manifest.mandatory) {
      // Reload into the new bundle now. notifyAppReady() (called on next boot
      // in initOta) commits it; if it white-screens, the watchdog rolls back.
      await updater.set(bundle);
    } else {
      // Stage it for the next cold start — less jarring than an in-session
      // reload. initOta() consumes this on the next launch via
      // activatePendingBundle(); manual mode won't auto-swap it otherwise.
      await updater.next(bundle);
    }
    console.info(
      `[ota] bundle ${manifest.version} ready (${manifest.mandatory ? 'applied now' : 'next launch'})`,
    );
  } catch (err) {
    console.warn('[ota] apply failed — keeping current bundle', err);
    if (downloadedId) {
      try {
        await updater.delete({ id: downloadedId });
      } catch {
        /* best effort cleanup */
      }
    }
  }
}

/**
 * Manual escape hatch: revert to the store-shipped bundle. Wire this to an
 * admin/debug control if a bad bundle slips past the watchdog.
 */
export async function resetToStoreBundle(): Promise<void> {
  if (!nativeCapacitor()) {
    return;
  }
  const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
  await CapacitorUpdater.reset(); // {} => built-in bundle
}

export const __OTA_SPIKE__ = { OTA_ENABLED, MANIFEST_URL };
